import { memo, useEffect, useState } from "react";
import { useLocation } from "../router.js";
import { Grid, Box, Button, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Image } from "mui-image";

import { countries, useSnackbar } from "../utils/index.js";
import { getCheckoutTier } from "../utils/team-tiers.js";
import CompanionLogo from "../assets/images/products/companion.png";
import PanoramaLogo from "../assets/images/products/panorama.png";
import TDDLogo from "../assets/images/products/tdd.png";
import Nis2Logo from "../assets/images/products/nis2.png";
import NonmaintainabilityLogo from "../assets/images/products/nonmaintainability.png";
import ProjectManagementLogo from "../assets/images/products/project_management.png";
import GuardLogo from "../assets/images/products/guard.png";
import CycloptAPILogo from "../assets/images/products/api.png";
import { createFree, createPayment } from "../api/index.js";

import theme from "../theme.js";

import { Form, Spinner } from "#components";

const useStyles = makeStyles((theme) => ({
	orderTitle: {
		color: "white",
		fontSize: "1rem",
		fontWeight: "bold",
	},
	total: {
		fontWeight: "bold",
		fontSize: "2.5rem",
		color: theme.palette.secondary.main,
	},
	grey: {
		color: "white",
		fontSize: "1rem",
		marginLeft: "0.2rem"
	},
	productBox: {
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between",
		padding: "5px 10px",
		margin: "0px",
		width: "100%",
		height: "70px",
	},
	productLetterBox: {
		width: "100%",
		padding: "0px 10px",
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		alignItems: "flex-start",
	},
	productLetter: {
		display: "flex",
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		width: "100%",
		color: "white",
	},
	productTitle: {
		fontWeight: "bold",
		color: theme.palette.secondary.main,
	},
	productAmount: {
		color: theme.palette.secondary.main,
	},
	formTitle: {
		color: theme.palette.primary.main,
		fontWeight: "bold",
		fontSize: "2.2rem",
		textAlign: "center",
		marginBottom: "10px",
	},
	leftArea: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		height: "calc(100vh - 74px)",
		backgroundColor: theme.palette.primary.main,
	},
	rightArea: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		height: "calc(100vh - 74px)",
	},
	inputTitles: {
		color: theme.palette.primary.main,
		fontWeight: "bold",
	},
}));

const Checkout = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [panoramaSeats, setPanoramaSeats] = useState(5);
	const [companionSeats, setCompanionSeats] = useState(10);
	const [tddReports, setTddReports] = useState(0);
	const [nis2Reports, setNis2Reports] = useState(0);
	const [nonmaintainabilityReports, setNonmaintainabilityReports] = useState(0);
	const [projectManagementSelected, setProjectManagementSelected] = useState(false);
	const [guardSelected, setGuardSelected] = useState(false);
	const [apiSelected, setApiSelected] = useState(false);
	const [freeSelected, setFreeSelected] = useState(false);
	const [companionTotal, setCompanionTotal] = useState(200);
	const [panoramaTotal, setPanoramaTotal] = useState(500);
	const [servicesTotal, setServicesTotal] = useState(0);
	const [total, setTotal] = useState(companionTotal + panoramaTotal + servicesTotal);
	const [totalOneOff, setTotalOneOff] = useState(0);
	const [isOneOff, setIsOneOff] = useState(false);
	const [recurringPeriod, setRecurringPeriod] = useState("monthly");
	const [recurringMultiplier, setRecurringMultiplier] = useState(1);
	const [invoice, setInvoice] = useState(false);
	const classes = useStyles();
	const search = useLocation().search;
	const tierRequested = new URLSearchParams(search).has("tier");
	const selectedTier = getCheckoutTier(new URLSearchParams(search).get("tier"));
	const billingTotal = selectedTier ? selectedTier.price * recurringMultiplier : total;
	const { error } = useSnackbar();

	useEffect(() => {
		const validCompanionSeats = Number.isNaN(companionSeats) ? 0 : companionSeats;
		const validPanoramaSeats = Number.isNaN(panoramaSeats) ? 0 : panoramaSeats;
		const validReports = (Number.isNaN(tddReports) ? 0 : tddReports)
			+ (Number.isNaN(nis2Reports) ? 0 : nis2Reports)
			+ (Number.isNaN(nonmaintainabilityReports) ? 0 : nonmaintainabilityReports);

		const tmpPanoramaTotal = validPanoramaSeats === 0 ? 0 : (validPanoramaSeats > 5 ? (500 + (validPanoramaSeats - 5) * 100) : 500);
		const tmpCompanionTotal = validCompanionSeats === 0 ? 0 : 20 * validCompanionSeats;
		const tmpServicesTotal = 10 * apiSelected + 10 * guardSelected + 50 * projectManagementSelected;
		setPanoramaTotal(tmpPanoramaTotal);
		setCompanionTotal(tmpCompanionTotal);
		setServicesTotal(tmpServicesTotal);
		setTotal((tmpPanoramaTotal + tmpCompanionTotal + tmpServicesTotal) * recurringMultiplier);
		setTotalOneOff(500 * validReports);
		setIsOneOff((tddReports > 0 || nis2Reports > 0 || nonmaintainabilityReports > 0));
	}, [companionSeats, panoramaSeats, tddReports, nis2Reports, nonmaintainabilityReports, projectManagementSelected, guardSelected, apiSelected, recurringMultiplier]);

	useEffect(() => {
		if (tierRequested) {
			setPanoramaSeats(0);
			setCompanionSeats(0);
			setTddReports(0);
			setNis2Reports(0);
			setNonmaintainabilityReports(0);
			setProjectManagementSelected(false);
			setGuardSelected(false);
			setApiSelected(false);
			setFreeSelected(false);
			return;
		}
		setPanoramaSeats(Number.parseInt(new URLSearchParams(search).get("panorama") ?? 0, 10));
		setCompanionSeats(Number.parseInt(new URLSearchParams(search).get("companion") ?? 0, 10));
		setTddReports(Number.parseInt(new URLSearchParams(search).get("tddReports") ?? 0, 10));
		setNis2Reports(Number.parseInt(new URLSearchParams(search).get("nis2Reports") ?? 0, 10));
		setNonmaintainabilityReports(Number.parseInt(new URLSearchParams(search).get("nonmaintainabilityReports") ?? 0, 10));

		setProjectManagementSelected((new URLSearchParams(search).get("projectManagement")) === "true" ?? false); // eslint-disable-line no-constant-binary-expression
		setGuardSelected((new URLSearchParams(search).get("cycloptGuard")) === "true" ?? false); // eslint-disable-line no-constant-binary-expression
		setApiSelected((new URLSearchParams(search).get("cycloptAPI")) === "true" ?? false); // eslint-disable-line no-constant-binary-expression
		const isFree = (new URLSearchParams(search).get("free")) === "true" ?? false; // eslint-disable-line no-constant-binary-expression
		setFreeSelected(isFree);
	}, [search, tierRequested]);

	useEffect(() => {
		setRecurringMultiplier(recurringPeriod === "monthly" ? 1 : 10);
	}, [recurringPeriod]);

	const formContent = [
		{
			customType: "input",
			id: "fullName",
			type: "text",
			label: "Full Name",
			placeholder: "Your name",
			labelColor: theme.palette.primary.main,
			inputProps: { sx: { borderRadius: 0 } },
		},
		{
			customType: "input",
			id: "email",
			type: "text",
			label: "Email Address",
			placeholder: "yourname@company.com",
			labelColor: theme.palette.primary.main,
			inputProps: { sx: { borderRadius: 0 } },
		},
		...(isOneOff || freeSelected ? [] : [{
			customType: "dropdown",
			id: "recurrence",
			label: "Payment Recurrence",
			labelColor: theme.palette.primary.main,
			items: [{ value: "monthly", text: "Monthly" }, { value: "annually", text: "Annually" }],
			size: "small",
			width: "200px",
			defaultValue: recurringPeriod,
			onChange: (val) => setRecurringPeriod(val),
		}]),
		...(freeSelected ? [{
			customType: "input",
			id: "role",
			type: "text",
			label: "Your Role",
			placeholder: "Your role",
			labelColor: theme.palette.primary.main,
			inputProps: { sx: { borderRadius: 0 } },
		}] : [{
			customType: "checkbox",
			id: "invoice",
			label: "Invoice",
			defaultValue: invoice,
			labelColor: theme.palette.primary.main,
			onChange: (value) => setInvoice(value),
		}]),
		...(invoice ? [
			{
				customType: "input",
				id: "companyName",
				type: "text",
				label: "Company Name",
				placeholder: "Your company name",
				labelColor: theme.palette.primary.main,
				inputProps: { sx: { borderRadius: 0 } },
			},
			{
				customType: "autocomplete",
				id: "country",
				label: "Country",
				options: countries.map((country) => (country.name)),
				size: "small",
				placeholder: "Select your country",
				labelColor: theme.palette.primary.main,
				width: "50%",
			},
			{
				customType: "input",
				id: "address",
				type: "text",
				label: "Address",
				placeholder: "Your address",
				labelColor: theme.palette.primary.main,
				inputProps: { sx: { borderRadius: 0 } },
			},
			{
				customType: "input",
				id: "postalCode",
				type: "text",
				label: "ZIP/Postal Code",
				placeholder: "Your ZIP/Postal Code",
				labelColor: theme.palette.primary.main,
				inputProps: { sx: { borderRadius: 0 } },
			},
			{
				customType: "input",
				id: "vat",
				type: "text",
				label: "VAT Number",
				placeholder: "Your VAT number",
				labelColor: theme.palette.primary.main,
				inputProps: { sx: { borderRadius: 0 } },
			}
		] : []
		),
		{
			customType: "button",
			id: "submit",
			type: "submit",
			color: "third",
			text: "Proceed",
		},
	];

	const onSubmit = async (values) => {
		if (tierRequested && !selectedTier) {
			error("Please select a valid team tier before proceeding.");
			return;
		}
		setIsLoading(true);
		const { success, message, url } = await (freeSelected ? createFree : createPayment)({
			...values,
			companionSeats,
			panoramaSeats,
			tddReports,
			nis2Reports,
			nonmaintainabilityReports,
			projectManagementSelected,
			guardSelected,
			apiSelected,
			recurringPeriod,
			total: billingTotal,
			...(selectedTier ? { tier: selectedTier.tier, linesOfCode: selectedTier.linesOfCode, monthlyPrice: selectedTier.price } : {}),
			totalOneOff,
			...(values?.country ? { countryCode: countries.find((c) => c.name === values?.country)?.code ?? "" } : {}),
		});
		setIsLoading(false);

		if (success) {
			window.location.href = url;
		} else {
			error(message);
		}
	};

	if (tierRequested && !selectedTier) {
		return (
			<Box sx={{ p: 4, textAlign: "center" }}>
				<Typography sx={{ mb: 2 }}>Please select an available team tier. For Enterprise plans, contact sales.</Typography>
				<Button component="a" href="../team-plan">Choose a team plan</Button>
				<Button component="a" href="https://cyclopt.com/contact">Contact sales</Button>
			</Box>
		);
	}

	return ([
		<Grid key={"randomKey"} sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", minHeight: "calc(100vh - 74px)" }}>
			<Grid container flexGrow={1} height="100%" display="flex" direction="row" alignItems="center" justifyContent="space-evenly">
				<Grid xs={12} md={6} className={classes.leftArea}>
					<Grid item container direction="column" sx={{ width: "400px" }}>
						<Grid item direction="row">
							<Typography className={classes.orderTitle}>{"Your order"}</Typography>
							{isOneOff && !freeSelected && (
								<Box display="flex" alignItems="baseline" pb="1rem">
									<Typography className={classes.total} >{`€${totalOneOff}`}</Typography>
									<Typography className={classes.grey} variant="body2">{"billed one-off"}</Typography>
									<Typography className={classes.grey} variant="body2">{"*"}</Typography>
								</Box>
							)}
							{!isOneOff && !freeSelected && (
								<Box display="flex" alignItems="baseline" pb="1rem">
									<Typography className={classes.total} >{`€${billingTotal}`}</Typography>
									<Typography className={classes.grey} variant="body2">{`billed ${recurringPeriod}`}</Typography>
									<Typography className={classes.grey} variant="body2">{"*"}</Typography>
								</Box>
							)}
							{freeSelected && (
								<Box display="flex" alignItems="baseline" pb="1rem">
									<Typography className={classes.total} >{`€0`}</Typography>
								</Box>
							)}
							{selectedTier ? (
								<Box sx={{ color: "white", py: 2, "& .MuiTypography-root": { fontSize: "0.9rem" } }}>
									<Box display="flex" justifyContent="space-between" sx={{ gap: 2, mb: 1 }}>
										<Typography className={classes.productTitle}>Team plan — Tier {selectedTier.tier}</Typography>
										<Typography className={classes.productAmount}>€{billingTotal}</Typography>
									</Box>
									<Typography sx={{ mb: 1 }}>Up to {selectedTier.linesOfCode.toLocaleString("en-US")} lines of code across your repositories</Typography>
									<Typography>Cyclopt Panorama + Cyclopt Companion included</Typography>
									<Typography sx={{ mt: 1, fontSize: "0.9rem" }}>Unlimited users, repositories and analyses</Typography>
								</Box>
							) : (
								<>
									{(!isOneOff && companionSeats > 0) && (
										<Box display="flex" alignItems="center" width="400px">
											<Grid className={classes.productBox}>
												<Image src={CompanionLogo} alt="Companion" width="60px" fit="contain" />
												<Grid item className={classes.productLetterBox}>
													<Grid item className={classes.productLetter}>
														<Typography className={classes.productTitle}>{"Cyclopt Companion"}</Typography>
														<Typography className={classes.productAmount}>{`€${freeSelected ? 0 : companionSeats * 20 * recurringMultiplier}`}</Typography>
													</Grid>
													<Grid item className={classes.productLetter}>
														{freeSelected && (
															<Grid direction="row" display="flex" fontSize="0.9rem">
																<Typography className={classes.grey} fontSize="inherit!important">
																	{"up to 30 commits per month"}
																</Typography>
															</Grid>
														)}
														{!freeSelected && (
															<Grid direction="row" display="flex" fontSize="0.9rem">
																<Typography className={classes.grey} fontSize="inherit!important">
																	{"Qty:"}
																</Typography>
																<Typography ml={1} fontWeight="bold" fontSize="inherit!important">
																	{companionSeats}
																</Typography>
															</Grid>
														)}
														{companionSeats > 1 && (
															<Typography className={classes.grey} fontSize="0.9rem!important">
																{`€${20 * recurringMultiplier} each`}
															</Typography>
														)}
													</Grid>
												</Grid>
											</Grid>
										</Box>
									)}
									{(!isOneOff && panoramaSeats > 0 && !freeSelected) && (
										<Box display="flex" alignItems="center" width="400px">
											<Grid className={classes.productBox}>
												<Image src={PanoramaLogo} alt="Panorama" width="60px" fit="contain" />
												<Grid item className={classes.productLetterBox}>
													<Grid item className={classes.productLetter}>
														<Typography className={classes.productTitle}>{"Cyclopt Panorama"}</Typography>
														<Typography className={classes.productAmount}>{`€${(panoramaSeats > 5 ? 500 + (panoramaSeats - 5) * 100 : 500) * recurringMultiplier}`}</Typography>
													</Grid>
													<Grid item className={classes.productLetter}>
														<Grid direction="row" display="flex" fontSize="0.9rem">
															<Typography className={classes.grey} fontSize="inherit!important">
																{"Qty:"}
															</Typography>
															<Typography ml={1} fontWeight="bold" fontSize="inherit!important">
																{panoramaSeats}
															</Typography>
														</Grid>
														{panoramaSeats > 5 && (
															<Typography className={classes.grey} fontSize="0.9rem!important">
																{`€${100 * recurringMultiplier} each after 5`}
															</Typography>
														)}
													</Grid>
												</Grid>
											</Grid>
										</Box>
									)}
									{(isOneOff && tddReports > 0 && !freeSelected) && (
										<Box display="flex" alignItems="center" width="400px">
											<Grid className={classes.productBox}>
												<Image src={TDDLogo} alt="TDD" width="60px" fit="contain" />
												<Grid item className={classes.productLetterBox}>
													<Grid item className={classes.productLetter}>
														<Typography className={classes.productTitle}>{"Technical Due Diligence"}</Typography>
														<Typography className={classes.productAmount}>{`€${500 * Number(tddReports)}`}</Typography>
													</Grid>
													<Grid item className={classes.productLetter}>
														<Grid direction="row" display="flex" fontSize="0.9rem">
															<Typography className={classes.grey} fontSize="inherit!important">
																{"Qty:"}
															</Typography>
															<Typography ml={1} fontWeight="bold" fontSize="inherit!important">
																{tddReports}
															</Typography>
														</Grid>
														{tddReports > 1 && (
															<Typography className={classes.grey} fontSize="0.9rem!important">
																{"€500 each"}
															</Typography>
														)}
													</Grid>
												</Grid>
											</Grid>
										</Box>
									)}
									{(isOneOff && nis2Reports > 0 && !freeSelected) && (
										<Box display="flex" alignItems="center" width="400px">
											<Grid className={classes.productBox}>
												<Image src={Nis2Logo} alt="NIS2" width="60px" fit="contain" />
												<Grid item className={classes.productLetterBox}>
													<Grid item className={classes.productLetter}>
														<Typography className={classes.productTitle}>{"Nis2"}</Typography>
														<Typography className={classes.productAmount}>{`€${500 * Number(nis2Reports)}`}</Typography>
													</Grid>
													<Grid item className={classes.productLetter}>
														<Grid direction="row" display="flex" fontSize="0.9rem">
															<Typography className={classes.grey} fontSize="inherit!important">
																{"Qty:"}
															</Typography>
															<Typography ml={1} fontWeight="bold" fontSize="inherit!important">
																{nis2Reports}
															</Typography>
														</Grid>
														{nis2Reports > 1 && (
															<Typography className={classes.grey} fontSize="0.9rem!important">
																{"€500 each"}
															</Typography>
														)}
													</Grid>
												</Grid>
											</Grid>
										</Box>
									)}
									{(isOneOff && nonmaintainabilityReports > 0 && !freeSelected) && (
										<Box display="flex" alignItems="center" width="400px">
											<Grid className={classes.productBox}>
												<Image src={NonmaintainabilityLogo} alt="Non-Maintainability Reports" width="60px" fit="contain" />
												<Grid item className={classes.productLetterBox}>
													<Grid item className={classes.productLetter}>
														<Typography className={classes.productTitle}>{"Non-Maintainability Reports"}</Typography>
														<Typography className={classes.productAmount}>{`€${500 * Number(nonmaintainabilityReports)}`}</Typography>
													</Grid>
													<Grid item className={classes.productLetter}>
														<Grid direction="row" display="flex" fontSize="0.9rem">
															<Typography className={classes.grey} fontSize="inherit!important">
																{"Qty:"}
															</Typography>
															<Typography ml={1} fontWeight="bold" fontSize="inherit!important">
																{nonmaintainabilityReports}
															</Typography>
														</Grid>
														{nonmaintainabilityReports > 1 && (
															<Typography className={classes.grey} fontSize="0.9rem!important">
																{"€500 each"}
															</Typography>
														)}
													</Grid>
												</Grid>
											</Grid>
										</Box>
									)}
									{(!isOneOff && projectManagementSelected && !freeSelected) && (
										<Box display="flex" alignItems="center" width="400px">
											<Grid className={classes.productBox}>
												<Image src={ProjectManagementLogo} alt="Project Management" width="60px" fit="contain" />
												<Grid item className={classes.productLetterBox}>
													<Grid item className={classes.productLetter}>
														<Typography className={classes.productTitle}>{"Cyclopt Project Management"}</Typography>
														<Typography className={classes.productAmount}>{`€${50 * recurringMultiplier}`}</Typography>
													</Grid>
													<Grid direction="row" display="flex" fontSize="0.9rem" color="white">
														<Typography className={classes.grey} fontSize="inherit!important" color="inherit!important">
															{"Qty:"}
														</Typography>
														<Typography ml={1} fontWeight="bold" fontSize="inherit!important" color="inherit!important">
															{1}
														</Typography>
													</Grid>
												</Grid>
											</Grid>
										</Box>
									)}
									{(!isOneOff && guardSelected && !freeSelected) && (
										<Box display="flex" alignItems="center" width="400px">
											<Grid className={classes.productBox}>
												<Image src={GuardLogo} alt="Cyclopt Guard" width="60px" fit="contain" />
												<Grid item className={classes.productLetterBox}>
													<Grid item className={classes.productLetter}>
														<Typography className={classes.productTitle}>{"Cyclopt Guard"}</Typography>
														<Typography className={classes.productAmount}>{`€${10 * recurringMultiplier}`}</Typography>
													</Grid>
													<Grid direction="row" display="flex" fontSize="0.9rem" color="white">
														<Typography className={classes.grey} fontSize="inherit!important" color="inherit!important">
															{"Qty:"}
														</Typography>
														<Typography ml={1} fontWeight="bold" fontSize="inherit!important" color="inherit!important">
															{1}
														</Typography>
													</Grid>
												</Grid>
											</Grid>
										</Box>
									)}
									{(!isOneOff && apiSelected && !freeSelected) && (
										<Box display="flex" alignItems="center" width="400px">
											<Grid className={classes.productBox}>
												<Image src={CycloptAPILogo} alt="Cyclopt API" width="60px" fit="contain" />
												<Grid item className={classes.productLetterBox}>
													<Grid item className={classes.productLetter}>
														<Typography className={classes.productTitle}>{"Cyclopt API"}</Typography>
														<Typography className={classes.productAmount}>{`€${10 * recurringMultiplier}`}</Typography>
													</Grid>
													<Grid item className={classes.productLetter}>
														<Grid direction="row" display="flex" fontSize="0.9rem">
															<Typography className={classes.grey} fontSize="inherit!important">
																{"Qty:"}
															</Typography>
															<Typography ml={1} fontWeight="bold" fontSize="inherit!important">
																{1}
															</Typography>
														</Grid>
													</Grid>
												</Grid>
											</Grid>
										</Box>
									)}
								</>
							)}
							{!freeSelected && (
								<Box sx={{ width: "100%", mt: 2.5, pt: 1.25, borderTop: "1px solid #e0eaf2" }}>
									<Typography sx={{ color: "white", fontSize: "0.9rem", m: 0 }}>
										{"* VAT will be calculated at checkout"}
									</Typography>
								</Box>
							)} 
						</Grid>
					</Grid>
				</Grid>
				<Grid xs={12} md={6} className={classes.rightArea}>
					<Grid item width="400px">
						<Typography className={classes.formTitle}>{"Order Details"}</Typography>
						<Form enableReinitialize content={formContent} onSubmit={onSubmit} />
					</Grid>
				</Grid>
			</Grid>
		</ Grid>,
		<Spinner key={"random_spinner_key"} open={isLoading} />,
	]);
};

export default memo(Checkout);
